import { useMapStoreContext } from 'app/src/editor/MapStoreContext';
import { UndoRedoContextProvider } from 'app/src/editor/UndoRedoContext';
import { LevelDescription } from 'app/src/LevelDescription';
import useForceUpdate from 'app/src/utils/useForceUpdate';
import * as key from 'keymaster';
import * as React from 'react';

type Props = {
	children: React.ReactNode;
};

export default function UndoRedoController({ children }: Props): React.ReactElement {
	const { mapStore } = useMapStoreContext();

	const markupStackRef = React.useRef<LevelDescription[]>([]);
	const currentIndexRef = React.useRef(0);

	const forceUpdate = useForceUpdate();

	// Create callbacks.
	const undo = React.useCallback((): void => {
		// If there's nothing in the undo stack or if we're at the
		// beginning of it, we can't undo.
		if (markupStackRef.current.length <= 1 || currentIndexRef.current <= 0) {
			return;
		}

		currentIndexRef.current -= 1;

		mapStore.setLevelDescription(markupStackRef.current[currentIndexRef.current]);

		forceUpdate();
	}, [forceUpdate, mapStore]);

	const redo = React.useCallback((): void => {
		// If we're at the end of the stack, there's nothing to redo.
		if (currentIndexRef.current === markupStackRef.current.length - 1) {
			return;
		}

		currentIndexRef.current += 1;

		mapStore.setLevelDescription(markupStackRef.current[currentIndexRef.current]);

		forceUpdate();
	}, [forceUpdate, mapStore]);

	const addUndoState = React.useCallback((): void => {
		// Update stack.
		let newMarkupStack = [...markupStackRef.current];

		// Trim any future redo states.
		if (currentIndexRef.current !== markupStackRef.current.length - 1) {
			newMarkupStack = newMarkupStack.slice(0, currentIndexRef.current + 1);
		}

		// Add new markup.
		newMarkupStack.push(mapStore.buildLevelDescription());
		markupStackRef.current = newMarkupStack;
		currentIndexRef.current = markupStackRef.current.length - 1;

		forceUpdate();
	}, [forceUpdate, mapStore]);

	// Create initial undo state (and additional states when the level is reset).
	React.useEffect(() => {
		addUndoState();
	}, [addUndoState, mapStore]);

	// Bind key handlers.
	React.useEffect(() => {
		key('command+z, ctrl+z', (e: KeyboardEvent) => {
			e.preventDefault();

			// Use of setTimeout is a hack to prevent keymaster from blowing up. When
			// undo is called, it causes keys to be unbound and rebound syncronously,
			// and this results in undo being called infinitely.
			setTimeout(() => undo(), 0);
		});
		key('command+shift+z, ctrl+shift+z', (e: KeyboardEvent) => {
			e.preventDefault();

			// Use of setTimeout is a hack to prevent keymaster from blowing up. When
			// redo is called, it causes keys to be unbound and rebound syncronously,
			// and this results in redo being called infinitely.
			setTimeout(() => redo(), 0);
		});

		return (): void => {
			key.unbind('ctrl+z');
			key.unbind('command+z');
			key.unbind('ctrl+shift+z');
			key.unbind('command+shift+z');
		};
	}, [redo, undo]);

	return (
		<UndoRedoContextProvider
			value={{
				undo,
				redo,
				addUndoState,
			}}
		>
			{children}
		</UndoRedoContextProvider>
	);
}
